import type { SubjectHistory } from '@/lib/scoreTrend';
import ScoreDeltaBadge from './ScoreDeltaBadge';

const BAR_MIN = 12;
const BAR_MAX = 54;

function barHeight(value: number, min: number, range: number): number {
  return BAR_MIN + ((value - min) / range) * (BAR_MAX - BAR_MIN);
}

export default function SubjectTrendChart({ history }: { history: SubjectHistory }) {
  const { points, latest, delta } = history;

  if (points.length === 1) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-background-50 px-3 py-3">
        <span className="w-16 shrink-0 truncate text-sm font-bold text-foreground-950">
          {history.subject}
        </span>
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-base font-bold text-primary-700">
          {latest}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-foreground-500">첫 시험 기록이에요.</p>
          <p className="mt-0.5 truncate text-[11px] text-foreground-400">{points[0].label}</p>
        </div>
        <ScoreDeltaBadge delta={delta} />
      </div>
    );
  }

  const values = points.map((p) => p.score);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-background-50 px-3 py-3">
      <div className="w-16 shrink-0">
        <p className="truncate text-sm font-bold text-foreground-950">{history.subject}</p>
        <p className="text-[11px] text-foreground-400">{points.length}회 기록</p>
      </div>

      <div className="flex min-w-0 flex-1 items-end justify-start gap-1.5 overflow-hidden">
        {points.map((p, idx) => {
          const isLatest = idx === points.length - 1;
          const height = barHeight(p.score, min, range);
          const tone = isLatest
            ? 'bg-primary-500'
            : p.mock
              ? 'bg-accent-300'
              : 'bg-primary-200';
          return (
            <div
              key={p.key}
              className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
              style={{ height: 92 }}
            >
              <span
                className={`text-[10px] font-semibold ${
                  isLatest ? 'text-primary-700' : 'text-foreground-500'
                }`}
              >
                {p.score}
              </span>
              <span
                className={`w-full max-w-[26px] rounded-t ${tone}`}
                style={{ height: `${height}px` }}
                title={`${p.label} · ${p.score}점`}
              />
              <span className="w-full truncate text-center text-[9px] text-foreground-400">
                {p.shortLabel}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex w-12 shrink-0 flex-col items-end gap-1">
        <span className="text-base font-bold text-foreground-950">{latest}</span>
        <ScoreDeltaBadge delta={delta} />
      </div>
    </div>
  );
}